import React from 'react';
import {
  PiCheckCircle, PiShieldCheck,
} from 'react-icons/pi';
import {
  PreviewFrame, Stars, avatarBg, initialsFor, fillTokens,
} from './_shared';

export default function RepeatedCtaTemplate({ sections, entity, viewport, theme, editable, onSectionClick }) {
  const banner    = sections.banner    || {};
  const hero      = sections.hero      || {};
  const benefits  = sections.benefits  || {};
  const reviews   = sections.reviews   || {};
  const guarantee = sections.guarantee || {};
  const accent    = theme?.primaryColor || '#1F1B33';
  const product   = entity || {};

  // Banner copy — headline hides on explicit ''; {discount} token filled from discountPct.
  // No fabricated 30% fallback: when discountPct is empty and the subtext relies on the
  // {discount} token, the honest no-discount sentence renders instead of token-stripped copy.
  const bannerHeadline = banner.headline ?? "Here's an exclusive offer to complement your order!";
  const hasDiscount = Number(banner.discountPct) > 0;
  const filledSubtext = (hasDiscount || !String(banner.subtext ?? '').includes('{discount}'))
    ? fillTokens(banner.subtext, { discount: hasDiscount ? String(banner.discountPct) : '' })
    : '';
  const bannerSubtext = filledSubtext
    || (hasDiscount
      ? `Add this to your order and get up to ${banner.discountPct}% off. We will ship it with your other items.`
      : 'Add this to your order. We will ship it with your other items.');

  // Repeated CTA block w/ honest decline link below — footnote is per-section, hides on explicit ''
  const CtaBlock = ({ ctaLabel, declineLabel, footnote }) => {
    const note = footnote ?? 'We will only ship it to you if you buy it now.';
    return (
      <div style={{ textAlign: 'center', marginTop: 18 }}>
        <button type="button" style={{
          background: '#1F1B33', color: '#fff', border: 'none',
          padding: '16px 40px', borderRadius: 6, fontSize: 14, fontWeight: 800,
          cursor: 'pointer', letterSpacing: 0.5, minWidth: 280,
          boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
        }}>
          {ctaLabel || 'YES, ADD THIS TO MY ORDER'}
        </button>
        {note && (
          <div data-lp-field="footnote" style={{ fontSize: 11, color: '#7d7a86', marginTop: 10 }}>
            {note}
          </div>
        )}
        {declineLabel && (
          <a style={{
            display: 'inline-block', marginTop: 6,
            fontSize: 11, color: '#9892a3', textDecoration: 'underline',
            fontStyle: 'italic', cursor: 'pointer',
          }}>
            {declineLabel}
          </a>
        )}
      </div>
    );
  };

  return (
    <PreviewFrame viewport={viewport} accent={accent} theme={theme} sections={sections} editable={editable} onSectionClick={onSectionClick}>
      {/* PERSONALIZED BANNER — light blue, greeting rendered verbatim */}
      <div data-lp-section="banner" style={{
        background: '#E0F0FF', color: '#1F4E8A', textAlign: 'center',
        padding: '14px 20px', borderBottom: '1px solid #BFD9F5',
      }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>
          <span data-lp-field="greeting" style={{ color: '#D9342B', fontWeight: 900 }}>{banner.greeting || 'Wait, friend!'}</span>
          {bannerHeadline ? <>{' '}<span data-lp-field="headline">{bannerHeadline}</span></> : null}
        </div>
        <div data-lp-field="subtext" style={{ fontSize: 12, color: '#1F4E8A', opacity: 0.85 }}>
          {bannerSubtext}
        </div>
      </div>

      {/* HERO — image + price + CTA #1 */}
      <section data-lp-section="hero" style={{ padding: viewport === 'mobile' ? '28px 20px' : '40px 48px', background: '#fff' }}>
        <div style={{
          maxWidth: 900, margin: '0 auto',
          display: 'grid', gap: 32,
          gridTemplateColumns: viewport === 'mobile' ? '1fr' : '0.9fr 1.1fr',
          alignItems: 'center',
        }}>
          {(() => {
            const heroImg = hero.heroImageUrl || product?.imageUrl;
            return (
              <div style={{
                aspectRatio: '1/1', borderRadius: 8,
                background: heroImg
                  ? `linear-gradient(135deg, #FBE9F1, #F8F4FF), url(${heroImg}) center/contain no-repeat`
                  : 'linear-gradient(135deg, #FBE9F1, #C4A8FF)',
                border: '1px solid #ECE8F5',
              }} />
            );
          })()}
          <div>
            <h1 style={{ fontSize: viewport === 'mobile' ? 24 : 30, fontWeight: 800, margin: '0 0 10px', color: '#1F1B33', lineHeight: 1.2, letterSpacing: -0.4 }}>
              {hero.headline || product?.title || 'Featured product'}
            </h1>
            {(hero.priceLabel || hero.compareAtLabel) ? (
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 14 }}>
                {hero.priceLabel ? (
                  <span data-lp-field="priceLabel" style={{ fontSize: 28, fontWeight: 800, color: '#1F1B33' }}>{hero.priceLabel}</span>
                ) : null}
                {hero.compareAtLabel && (
                  <span style={{ fontSize: 16, color: '#9892a3', textDecoration: 'line-through' }}>{hero.compareAtLabel}</span>
                )}
              </div>
            ) : null}
            <p style={{ fontSize: 14, color: '#4a4458', lineHeight: 1.55, margin: '0 0 16px' }}>
              {hero.description || 'If you use this on a daily basis, take advantage of this exclusive bundle pricing today.'}
            </p>
            <CtaBlock ctaLabel={hero.ctaLabel} declineLabel={hero.declineLabel} footnote={hero.footnote} />
          </div>
        </div>
      </section>

      {/* BENEFITS — checklist on light purple, CTA #2 */}
      <section data-lp-section="benefits" style={{ padding: viewport === 'mobile' ? '40px 24px' : '56px 48px', background: '#F1ECFF' }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <h2 style={{ fontSize: viewport === 'mobile' ? 18 : 22, fontWeight: 800, margin: '0 0 24px', textAlign: 'center', color: '#1F1B33', letterSpacing: -0.3 }}>
            {benefits.headline || 'Add this to your cart with all its amazing benefits'}
          </h2>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {(benefits.items || []).map((it, i) => (
              <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, color: '#1F1B33', lineHeight: 1.5 }}>
                <span style={{
                  width: 20, height: 20, borderRadius: '50%', background: '#22B364',
                  color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, marginTop: 1, fontSize: 12, fontWeight: 900,
                }}>✓</span>
                <span>{it}</span>
              </li>
            ))}
          </ul>
          <CtaBlock ctaLabel={benefits.ctaLabel} declineLabel={benefits.declineLabel} footnote={benefits.footnote} />
        </div>
      </section>

      {/* REVIEWS — 4-up grid w/ avatars, CTA #3 (only when real reviews exist) */}
      {(() => {
        const reviewItems = (reviews.items || reviews.reviews || [])
          .filter((r) => ((r?.quote || r?.body || '').trim()));
        if (reviewItems.length === 0) return null;
        const subheadline = reviews.subheadline ?? 'Real shoppers, real reviews — verified by us';
        const verifiedLabel = reviews.verifiedLabel ?? 'Verified buyer';
        return (
          <section data-lp-section="reviews" style={{ padding: viewport === 'mobile' ? '40px 24px' : '56px 48px', background: '#fff' }}>
            <div style={{ maxWidth: 800, margin: '0 auto' }}>
              <h2 style={{ fontSize: viewport === 'mobile' ? 20 : 26, fontWeight: 800, margin: subheadline ? '0 0 6px' : '0 0 28px', textAlign: 'center', letterSpacing: -0.4 }}>
                {reviews.headline || 'Why People Love Us'}
              </h2>
              {subheadline ? (
                <p data-lp-field="subheadline" style={{ fontSize: 13, color: '#7d7a86', textAlign: 'center', margin: '0 0 28px' }}>
                  {subheadline}
                </p>
              ) : null}
              <div style={{
                display: 'grid', gap: 16,
                gridTemplateColumns: viewport === 'mobile' ? '1fr' : '1fr 1fr',
              }}>
                {reviewItems.map((r, i) => {
                  const initials = initialsFor(r, i);
                  return (
                    <div key={i} style={{ display: 'flex', gap: 12 }}>
                      <div style={{
                        width: 48, height: 48, borderRadius: '50%',
                        background: avatarBg(r.name || initials || `R${i}`), color: '#1F1B33',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 14, fontWeight: 700, flexShrink: 0,
                      }}>
                        {initials}
                      </div>
                      <div>
                        {(r.stars ?? r.rating) != null && (r.stars ?? r.rating) !== '' && <Stars n={Number(r.stars ?? r.rating)} size={12} />}
                        <p style={{ fontSize: 13, color: '#1F1B33', margin: '6px 0 6px', lineHeight: 1.5 }}>
                          "{r.quote || r.body}"
                        </p>
                        {(r.name || r.verified === true) && (
                          <div style={{ fontSize: 11, color: '#7d7a86', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            {r.verified === true && (
                              <PiCheckCircle size={11} weight="fill" style={{ color: '#22B364' }} />
                            )}
                            {r.name}
                            {r.verified === true && verifiedLabel ? (
                              <span data-lp-field="verifiedLabel">· {verifiedLabel}</span>
                            ) : null}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <CtaBlock ctaLabel={reviews.ctaLabel} declineLabel={reviews.declineLabel} footnote={reviews.footnote} />
            </div>
          </section>
        );
      })()}

      {/* GUARANTEE + FINAL CTA */}
      <section data-lp-section="guarantee" style={{ padding: viewport === 'mobile' ? '40px 24px' : '56px 32px', background: '#FAFAFC', textAlign: 'center', borderTop: '1px solid #ECE8F5' }}>
        <div style={{
          width: 64, height: 64, borderRadius: '50%', background: '#fff',
          border: '2px solid #22B364', display: 'inline-flex',
          alignItems: 'center', justifyContent: 'center', marginBottom: 14,
        }}>
          <PiShieldCheck size={32} style={{ color: '#22B364' }} weight="fill" />
        </div>
        <h2 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 10px', letterSpacing: -0.3 }}>
          {guarantee.headline || 'You are covered by our 60-day guarantee'}
        </h2>
        <p style={{ fontSize: 14, color: '#4a4458', maxWidth: 560, margin: '0 auto', lineHeight: 1.55 }}>
          {guarantee.body || 'Try it risk-free. If you don\'t love it, send it back for a full refund.'}
        </p>
        <CtaBlock ctaLabel={guarantee.ctaLabel} declineLabel={null} footnote={guarantee.footnote} />
      </section>
    </PreviewFrame>
  );
}

